import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { AccommodationCombinedType } from './components/Accommodations/components/Accommodations/Accommodation.types';
import { AccommodationsHotelsCounts } from './components/Accommodations/components/AccommodationsHotelsCount/useAccommodationsHotelsCounts';
import { AccommodationsPrices } from './components/Accommodations/hooks/useAccommodationsPrices';

const category = 'wedding';
const pagePrefix = 'guest';
const action = 'GuestSiteAccommodationInteracted';

interface hotelExtraInfo {
  hotelName?: string;
  isHotelPreferred?: boolean;
  hotelPrice?: number;
  hotelStrikeThroughPrice?: number;
  clickedFrom?: 'tileFavorite' | 'dialog';
  type?: AccommodationCombinedType;
}

export interface PriceInfo {
  joyPrice?: string;
  expediaPrice?: string;
  bookingPrice?: string;
  priceComparisonResult: 'joywins' | 'externalwins' | 'joypricenotfound' | 'externalpricenotfound';
}

type HotelInfo = Readonly<{
  eventHandle: string;
  accommodationPlaceId: string;
  hotelName: string;
  venueName?: string;
  milesFromVenue?: string;
}>;

const actionProps = {
  action,
  category,
  pagePrefix,
};

const telemObject = createTelemetryObject({
  pages: {
    customPage: (page: string) => ({
      category,
      pagePrefix,
      page,
    }),
    remindMeLaterDialog: () => ({
      category: 'EventAccommodations',
      name: 'guest.wedding.accommodations.remindMeLaterDialog',
      page: 'remindMeLaterDialog',
      pagePrefix: 'guest',
    }),
  },
  actions: {
    shareRoomClicked: (page: string, args: hotelExtraInfo) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'shareRoomClicked',
        ...args,
      },
    }),
    hotelPinSelected: (page: string, args: hotelExtraInfo) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'hotelPinSelected',
        ...args,
      },
    }),
    hotelPinCardSelected: (page: string, args: hotelExtraInfo) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'hotelPinCardSelected',
        ...args,
      },
    }),
    accommodationsMapClicked: (page: string) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'accommodationsMapClicked',
      },
    }),
    mapExpanded: (page: string) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'mapExpanded',
      },
    }),
    mapContracted: (page: string) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'mapContracted',
      },
    }),
    exploreOnMapClicked: (page: string) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'exploreOnMapClicked',
      },
    }),
    mapMarkerClicked: (page: string, args: { hotelId: string; hotelName?: string; from: 'miniMap' | 'fullMap' }) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'mapMarkerClicked',
        ...args,
      },
    }),
    onClickConciergeEmailLink: (page: string, link: string) => ({
      ...actionProps,
      page,
      extraInfo: {
        link,
        name: 'linkClicked',
        source: 'AccommodationsNote',
      },
    }),
    leadGenBannerCtaClicked: () => ({
      category: 'GuestAccommodations',
      action: 'TravelButtonInteracted',
      label: 'getStarted',
      extraInfo: {
        name: 'leadGenBanner',
      },
    }),
    leadGenDialogCtaClicked: () => ({
      category: 'GuestAccommodations',
      action: 'TravelButtonInteracted',
      label: 'getStarted',
      extraInfo: {
        name: 'leadGenDialog',
      },
    }),
    leadGenDialogPromptedClicked: () => ({
      category: 'GuestAccommodations',
      action: 'TravelButtonInteracted',
      label: 'selectRooms',
      extraInfo: {
        name: 'leadGenDialog_prompted',
      },
    }),
    leadGenDialogDismissClicked: () => ({
      category: 'GuestAccommodations',
      action: 'TravelButtonInteracted',
      label: 'Close',
      extraInfo: {
        name: 'leadGenDialog_dismiss',
      },
    }),
    mapItemClicked: (page: string, args: hotelExtraInfo) => ({
      ...actionProps,
      page,
      extraInfo: {
        name: 'mapItemClicked',
        ...args,
      },
    }),
    viewHotelPDPPage: (hotel: HotelInfo, prices?: AccommodationsPrices, available?: Maybe<boolean>) => ({
      category: 'EventAccommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'ViewHotel',
        hotel,
        available,
        ...prices,
      },
    }),
    accommodationsLinkInteracted: (url: string, args: object, source: string = 'accommodations') => ({
      action: 'LinkInteracted',
      category: 'linkify',
      label: 'accommodations',
      extraInfo: {
        actionType: 'click',
        url: url,
        source,
        type: 'url',
        ...args,
      },
    }),
    photoCardClicked: (eventId: string, albumId: string, mediaId: string, photoUrl: string) => ({
      action: 'PhotoGalleryInteracted',
      category,
      extraInfo: {
        name: 'ViewPhotoClicked',
        eventId,
        albumId,
        mediaId,
        photoUrl,
      },
    }),
    formFieldInteracted: (label: string, inputAction: 'onFocus' | 'onChange') => ({
      category: 'EventAccommodations',
      action: 'FormFieldInteracted',
      label,
      inputAction,
      extraInfo: {
        name: 'remind_to_book',
      },
    }),
    remindMeLaterFormSubmitted: (duration: string, reminderDate: string) => ({
      category: 'EventAccommodations',
      action: 'FormSubmitted',
      extraInfo: {
        duration,
        reminderDate,
        name: 'remind_to_book',
      },
    }),
    remindMeLaterDialogDismissed: () => ({
      category: 'EventAccommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'remind_to_book_dismiss_cta',
      },
    }),
    remindMeLaterDialogOutsideClick: () => ({
      category: 'EventAccommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'remind_to_book_dismiss_click_outside',
      },
    }),
    openAccommodationsPriceCompareDialog: (page: string, args: hotelExtraInfo, prices: PriceInfo) => ({
      ...actionProps,
      label: 'priceCompare',
      page,
      extraInfo: {
        name: 'priceCompare',
        ...args,
        ...prices,
      },
    }),
    guestAccommodationsData: (page: string, args: AccommodationsHotelsCounts) => ({
      ...actionProps,
      action: 'TravelButtonInteracted',
      label: 'guestAccommodationsData',
      page,
      extraInfo: {
        name: 'guestAccommodationsData',
        ...args,
      },
    }),
  },
});

const { TelemetryProvider: customPageTelemetryProvider, useTelemetry: useCustomPageTelemetry } = createTelemetry(telemObject);

export { customPageTelemetryProvider, useCustomPageTelemetry };
